import { useState } from 'react';
import { Box, Flex, Text, Tooltip, IconButton, Spinner } from '@chakra-ui/react';
import { Link, useLocation } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Bookmark, X, Pencil, Check, GripVertical } from 'lucide-react';

import { useAtalhos } from '../../contexts/atalhos';
import { iconeDaPagina } from './icones';

const ACCENT = '#F68633';

/**
 * Atalhos escolhidos pelo usuário, exibidos ao lado do logo.
 *
 * Só no desktop: no mobile a navbar não tem espaço e a navegação é pela gaveta.
 * O modo de edição segue a ideia do Mercado Livre (arrastar para reordenar, um
 * "x" por chip e um botão de concluir), com a identidade visual do TAGA.
 */
const AtalhosNavbar = () => {
  const { atalhos, max, carregando, removerAtalho, reordenarAtalhos, salvando, reordenando } = useAtalhos();
  const [editando, setEditando] = useState(false);
  const { pathname } = useLocation();

  if (carregando) return null;

  const vazio = atalhos.length === 0;

  const aoSoltar = (resultado) => {
    if (!resultado.destination) return;
    reordenarAtalhos(resultado.source.index, resultado.destination.index);
  };

  const Chip = ({ atalho, arrastando, dragHandleProps }) => {
    const Icone = iconeDaPagina(atalho.pagina);
    const gravando = salvando(atalho.pagina);
    const ativo = !editando
      && (pathname === atalho.pagina || pathname.startsWith(atalho.pagina + '/'));

    return (
      <Flex
        align="center" gap={1.5}
        px={2.5} py={1} borderRadius="full"
        maxW="190px"
        bg={arrastando ? 'orange.50' : (ativo ? 'orange.50' : 'gray.50')}
        border={arrastando ? '1px dashed' : '1px solid'}
        borderColor={arrastando ? ACCENT : (ativo ? 'orange.200' : 'gray.200')}
        color={ativo || arrastando ? ACCENT : 'gray.600'}
        fontWeight={ativo ? 'semibold' : 'medium'}
        boxShadow={arrastando ? 'md' : 'none'}
        opacity={gravando ? 0.65 : 1}
        transition="background 0.15s, border-color 0.15s, color 0.15s, opacity 0.15s"
        _hover={editando ? {} : { bg: ativo ? 'orange.100' : 'gray.100', borderColor: ativo ? 'orange.300' : 'gray.300' }}
        cursor={editando ? 'default' : 'pointer'}
        userSelect="none"
        aria-busy={gravando}
      >
        {editando && (
          <Box
            {...dragHandleProps}
            color="gray.400" cursor="grab" display="flex" alignItems="center"
            _active={{ cursor: 'grabbing' }} _hover={{ color: ACCENT }}
            aria-label={`Arrastar ${atalho.label}`}
          >
            <GripVertical size={11} />
          </Box>
        )}

        {/* Durante a gravação o ícone da tela vira o spinner, no mesmo lugar. */}
        <Box flexShrink={0} display="flex" alignItems="center" justifyContent="center" w="12px">
          {gravando
            ? <Spinner size="xs" speed="0.6s" thickness="2px" color={ACCENT} boxSize="11px" />
            : <Icone size={12} />}
        </Box>
        <Text fontSize="xs" isTruncated>{atalho.label}</Text>

        {editando && (
          <Box
            as="button" type="button"
            onClick={() => removerAtalho(atalho.pagina)}
            isDisabled={gravando}
            color="gray.400" _hover={{ color: gravando ? 'gray.400' : 'red.500' }}
            display="flex" alignItems="center" transition="color 0.12s"
            cursor={gravando ? 'wait' : 'pointer'}
            aria-label={`Remover atalho ${atalho.label}`}
          >
            <X size={12} />
          </Box>
        )}
      </Flex>
    );
  };

  return (
    <Flex display={{ base: 'none', lg: 'flex' }} alignItems="center" gap={1.5} ml={3} minW={0}>
      {vazio ? (
        <Tooltip
          label="Abra o menu lateral e clique na bandeira ao lado de uma tela para fixá-la aqui"
          placement="bottom" hasArrow bg="gray.700" fontSize="xs" openDelay={300}
        >
          <Flex
            align="center" gap={1.5} px={2.5} py={1} borderRadius="full"
            border="1px dashed" borderColor="gray.300" color="gray.400"
            cursor="default" userSelect="none"
          >
            <Bookmark size={12} />
            <Text fontSize="xs" fontWeight="medium">Sem atalhos</Text>
          </Flex>
        </Tooltip>
      ) : (
        <DragDropContext onDragEnd={aoSoltar}>
          <Droppable droppableId="atalhos-navbar" direction="horizontal" isDropDisabled={!editando}>
            {(dropProvided) => (
              <Flex
                ref={dropProvided.innerRef}
                {...dropProvided.droppableProps}
                alignItems="center" gap={1.5} minW={0}
              >
                {atalhos.map((atalho, i) => (
                  <Draggable
                    key={atalho.pagina}
                    draggableId={atalho.pagina}
                    index={i}
                    isDragDisabled={!editando}
                  >
                    {(dragProvided, snapshot) => (
                      <Box
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        style={dragProvided.draggableProps.style}
                        minW={0}
                      >
                        {editando ? (
                          <Chip
                            atalho={atalho}
                            arrastando={snapshot.isDragging}
                            dragHandleProps={dragProvided.dragHandleProps}
                          />
                        ) : (
                          <Link to={atalho.pagina}>
                            <Chip atalho={atalho} />
                          </Link>
                        )}
                      </Box>
                    )}
                  </Draggable>
                ))}
                {dropProvided.placeholder}
              </Flex>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {!vazio && (
        <Tooltip
          label={editando ? 'Concluir' : `Editar atalhos (${atalhos.length}/${max})`}
          placement="bottom" hasArrow fontSize="xs" openDelay={300}
        >
          <IconButton
            aria-label={editando ? 'Concluir edição dos atalhos' : 'Editar atalhos'}
            icon={reordenando
              ? <Spinner size="xs" speed="0.6s" thickness="2px" color={ACCENT} boxSize="12px" />
              : (editando ? <Check size={13} /> : <Pencil size={12} />)}
            size="xs" variant="ghost" borderRadius="full"
            color={editando ? ACCENT : 'gray.400'}
            _hover={{ color: ACCENT, bg: 'orange.50' }}
            isDisabled={reordenando}
            onClick={() => setEditando((v) => !v)}
          />
        </Tooltip>
      )}
    </Flex>
  );
};

export default AtalhosNavbar;
